/* eslint-disable complexity */
/* eslint-disable max-lines */
import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { DatePicker, DateRange } from '@com-delta-omnichannel/mach-core-js';
import { DatePickerError } from '@com-delta-omnichannel/mach/lib/components/organisms/date-picker/models/date-picker.interfaces';
import { TripTypeOptions } from '../../../../constants/book-widget.constants';
import { BookWidgetAem } from '../../../../models/book-widget-aem.interfaces';
import {
  DatepickerConfig,
  SelectedRange
} from '../../../../models/common.interfaces';
import { ErrorStateDirective } from '../../directive';
import { formatDate, generateRandomIdString } from '../mach-core-utils';

@Component({
  standalone: true,
  selector: 'idp-date-picker-mach-core',
  templateUrl: './date-picker-mach-core.component.html',
  styleUrls: ['./date-picker-mach-core.component.scss'],
  imports: [NgIf, ErrorStateDirective, NgClass]
})
export class DatePickerMachCoreComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() selectedTripType!: TripTypeOptions;
  @Input() bookWidgetAem!: BookWidgetAem;
  @Input() datepickerConfig!: DatepickerConfig;
  @Input() selectedRange!: DateRange;
  @Input() fieldErrorInfo: DatePickerError | undefined = undefined;

  @Output() selectedDateRange = new EventEmitter<SelectedRange>();
  datePicker!: DatePicker;
  protected triggerElementId = '';
  protected datepickerElementId = '';
  protected selectedDatepickerValue = '';
  protected ariaLabelSelectedDate = '';

  showDatePicker = false;

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {}

  ngOnInit() {
    this.triggerElementId = this.createElementId('date-picker-trigger');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedRange']) {
      this.selectedDatepickerValue = this.setFormattedDateRange(
        this.selectedRange.start,
        this.selectedRange.end
      );
      this.setDateOnChange();
    }

    if (
      changes['selectedTripType'] &&
      !changes['selectedTripType'].isFirstChange()
    ) {
      this.selectedDatepickerValue = this.setFormattedDateRange(
        this.selectedRange.start,
        this.selectedRange.end
      );
      this.initializeDatePicker();
    }
  }

  getAriaLabelSelectedDate(): string {
    const {
      departAriaLabel = '',
      returnAriaLabel = '',
      datesAriaLabel = ''
    } = this.bookWidgetAem.datesField;

    const start = `${departAriaLabel} ${formatDate(this.selectedRange?.start)}`;
    const end = `${returnAriaLabel} ${formatDate(this.selectedRange?.end)}`;

    if (this.selectedTripType === TripTypeOptions.ROUND_TRIP) {
      return `${datesAriaLabel}, ${start} - ${end}`;
    }

    return `${datesAriaLabel}, ${start}`;
  }

  setDateOnChange() {
    if (this.datePicker) {
      if (this.selectedTripType === TripTypeOptions.ROUND_TRIP) {
        if (this.selectedRange.start && this.selectedRange.end) {
          this.datePicker.selectRange(
            this.selectedRange.start,
            this.selectedRange.end
          );
        }
      } else if (this.selectedRange.start) {
        this.datePicker.selectDate(this.selectedRange.start);
      }
    }
  }

  protected getSelectionType() {
    return this.selectedTripType === TripTypeOptions.ROUND_TRIP
      ? 'range'
      : 'single';
  }

  ngAfterViewInit() {
    this.initializeDatePicker();
  }

  protected initializeDatePicker() {
    if (this.datePicker) {
      this.datePicker.destroy();
    }

    this.datepickerElementId = this.createElementId('date-picker');
    // Create the date-picker div dynamically
    const datePickerDiv = this.renderer.createElement('div');
    this.renderer.addClass(datePickerDiv, 'date-picker');
    this.renderer.setAttribute(datePickerDiv, 'id', this.datepickerElementId);
    this.renderer.appendChild(this.elementRef.nativeElement, datePickerDiv);

    setTimeout(() => {
      this.datePicker = new DatePicker({
        id: this.datepickerElementId,
        title: this.bookWidgetAem?.datesField?.chooseDatesLabel || '',
        selectionType: this.getSelectionType(),
        triggerElementId: this.triggerElementId,
        shouldResetViewOnClear: false,
        dates: {
          dateRange: {
            start: this.datepickerConfig.startDate,
            end: this.datepickerConfig.endDate
          },
          selectedRange: {
            start: this.selectedRange?.start,
            end: this.selectedRange?.end
          },
          selectedDate: this.selectedRange?.start ?? undefined
        },
        //need to add this in store and get from bookWidgetAem
        footer: {
          primaryButtonLabel: 'Done',
          clearButtonLabel: 'Clear'
        },
        monthView: 'dual'
      });
      this.setupEventHandler();
      this.setDateOnChange();
    }, 0);
  }

  protected getAemLabels() {
    const {
      datesField: { depart: departLabel = '', return: returnLabel = '' } = {}
    } = this.bookWidgetAem || {};
    return { departLabel, returnLabel };
  }

  protected setupEventHandler() {
    this.datePicker.onChange(() => {
      const selectedDates = this.datePicker?.getSelectedDates();
      this.selectedDatepickerValue = this.setFormattedDateRange(
        selectedDates.start,
        selectedDates.end
      );
      this.selectedDateRange.emit(this.getSelectedRange(selectedDates));
    });

    this.datePicker.onClose(() => {
      this.showDatePicker = false;
    });
  }

  private getSelectedRange(selectedDates: DateRange) {
    if (!selectedDates.start && !selectedDates.end)
      return { start: null, end: null, total: 0 };
    if (!selectedDates.end) {
      return { start: selectedDates.start, end: null, total: 1 };
    }
    if (selectedDates.start && selectedDates.end) {
      const diff = selectedDates.end.getTime() - selectedDates.start.getTime();
      const dayInMs = 86400000;
      return {
        start: selectedDates.start,
        end: selectedDates.end,
        total: Math.ceil(diff / dayInMs)
      };
    }
    return;
  }

  protected createElementId(element: 'date-picker-trigger' | 'date-picker') {
    return `${element}-${generateRandomIdString()}`;
  }

  protected setFormattedDateRange(
    departDate: Date | null,
    returnDate: Date | null
  ) {
    let { departLabel, returnLabel } = this.getAemLabels();
    if (departDate) {
      departLabel = departDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
    if (returnDate) {
      returnLabel = returnDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }

    this.ariaLabelSelectedDate = this.getAriaLabelSelectedDate();
    return this.selectedTripType === TripTypeOptions.ROUND_TRIP
      ? `${departLabel} - ${returnLabel}`
      : `${departLabel}`;
  }

  toggleDatePickerOpenState() {
    this.showDatePicker = !this.showDatePicker;

    if (this.showDatePicker) {
      this.datePicker.open();
    } else {
      this.datePicker.close();
    }
  }

  ngOnDestroy(): void {
    if (this.datePicker) {
      this.datePicker.destroy();
    }
  }
}
